import { safeEvaluate } from '../../utils/expression';

export type RuleSeverity = 'critical' | 'warning' | 'info';

export interface Rule {
  id: string;
  name: string;
  condition: string;
  severity: RuleSeverity;
  message: string;
  recommendation?: string;
  enabled: boolean;
}

export interface RuleContext {
  currentFreq: number;
  currentAmp: number;
  dcVoltage: number;
  heatsinkTemp: number;
  load: number;
  imbalance: number;
  fanMainHours: number;
  fanControlHours: number;
  daysOffline: number;
  voltageDrop: number;
  looseTerminal: boolean;
  cloggedHeatsink: boolean;
  nominalCurrent: number;
  nominalDC: number;
  maxTemp: number;
}

export interface RuleResult {
  ruleId: string;
  ruleName: string;
  triggered: boolean;
  severity: RuleSeverity;
  message: string;
  recommendation?: string;
  evaluatedAt: Date;
}

const DEFAULT_RULES: Rule[] = [
  {
    id: 'temp-high',
    name: 'Temperatura Elevada',
    condition: 'heatsinkTemp > maxTemp * 0.85',
    severity: 'warning',
    message: 'Temperatura do dissipador acima de 85% do máximo',
    recommendation: 'Verificar ventilação e carga do inversor',
    enabled: true,
  },
  {
    id: 'temp-critical',
    name: 'Temperatura Crítica',
    condition: 'heatsinkTemp >= maxTemp',
    severity: 'critical',
    message: 'Temperatura do dissipador no limite máximo',
    recommendation: 'Reduzir carga imediatamente. Verificar sistema de arrefecimento.',
    enabled: true,
  },
  {
    id: 'overcurrent',
    name: 'Sobrecorrente',
    condition: 'currentAmp > nominalCurrent * 1.1',
    severity: 'warning',
    message: 'Corrente acima de 110% do nominal',
    recommendation: 'Verificar carga do motor e condições mecânicas',
    enabled: true,
  },
  {
    id: 'overcurrent-critical',
    name: 'Sobrecorrente Crítica',
    condition: 'currentAmp > nominalCurrent * 1.5',
    severity: 'critical',
    message: 'Corrente acima de 150% do nominal — risco de disparo',
    recommendation: 'Reduzir carga imediatamente',
    enabled: true,
  },
  {
    id: 'dc-overvoltage',
    name: 'Sobretensão CC',
    condition: 'dcVoltage > nominalDC * 1.25',
    severity: 'warning',
    message: 'Tensão do barramento CC elevada',
    recommendation: 'Verificar alimentação de entrada e freamento',
    enabled: true,
  },
  {
    id: 'dc-undervoltage',
    name: 'Subtensão CC',
    condition: 'dcVoltage < nominalDC * 0.75',
    severity: 'warning',
    message: 'Tensão do barramento CC baixa',
    recommendation: 'Verificar alimentação de entrada',
    enabled: true,
  },
  {
    id: 'imbalance-high',
    name: 'Desequilíbrio de Fases',
    condition: 'imbalance > 10',
    severity: 'warning',
    message: 'Desequilíbrio entre fases detectado',
    recommendation: 'Verificar tensões de alimentação e contatos',
    enabled: true,
  },
  {
    id: 'fan-wear',
    name: 'Desgaste do Ventilador Principal',
    condition: 'fanMainHours > 45000',
    severity: 'warning',
    message: 'Ventilador principal próximo do fim de vida (45.000h)',
    recommendation: 'Programar substituição preventiva do ventilador',
    enabled: true,
  },
  {
    id: 'fan-critical',
    name: 'Ventilador Principal Crítico',
    condition: 'fanMainHours > 50000',
    severity: 'critical',
    message: 'Ventilador principal excedeu vida útil (50.000h)',
    recommendation: 'Substituir ventilador imediatamente',
    enabled: true,
  },
  {
    id: 'capacitor-reforming',
    name: 'Reforming de Capacitores',
    condition: 'daysOffline > 180',
    severity: 'warning',
    message: 'Inversor inativo por mais de 180 dias',
    recommendation: 'Aplicar protocolo de reforming antes da energização',
    enabled: true,
  },
  {
    id: 'capacitor-critical',
    name: 'Reforming Crítico',
    condition: 'daysOffline > 365',
    severity: 'critical',
    message: 'Inversor inativo por mais de 365 dias — risco de explosão',
    recommendation: 'Reforming obrigatório com fonte CC regulada',
    enabled: true,
  },
  {
    id: 'loose-terminal',
    name: 'Conexão Frouxa no DC Link',
    condition: 'looseTerminal == true',
    severity: 'warning',
    message: 'Ondulação anormal no barramento CC — terminal frouxo',
    recommendation: 'Torquear bornes de potência conforme manual',
    enabled: true,
  },
  {
    id: 'clogged-heatsink',
    name: 'Dissipador Obstruído',
    condition: 'cloggedHeatsink == true',
    severity: 'warning',
    message: 'Restrição térmica no dissipador',
    recommendation: 'Realizar limpeza do canalete de ventilação',
    enabled: true,
  },
];

export class RulesEngine {
  private rules: Rule[];

  constructor(rules?: Rule[]) {
    this.rules = rules ?? DEFAULT_RULES;
  }

  evaluate(context: RuleContext): RuleResult[] {
    const results: RuleResult[] = [];
    const ctx = this.contextToRecord(context);

    for (const rule of this.rules) {
      if (!rule.enabled) continue;

      try {
        const triggered = safeEvaluate(rule.condition, ctx) === true;

        results.push({
          ruleId: rule.id,
          ruleName: rule.name,
          triggered,
          severity: rule.severity,
          message: rule.message,
          recommendation: rule.recommendation,
          evaluatedAt: new Date(),
        });
      } catch (err) {
        results.push({
          ruleId: rule.id,
          ruleName: rule.name,
          triggered: false,
          severity: 'info',
          message: `Erro ao avaliar regra: ${err instanceof Error ? err.message : 'desconhecido'}`,
          evaluatedAt: new Date(),
        });
      }
    }

    return results;
  }

  getTriggered(context: RuleContext): RuleResult[] {
    return this.evaluate(context).filter((r) => r.triggered);
  }

  getRules(): Rule[] {
    return [...this.rules];
  }

  addRule(rule: Rule): void {
    this.rules.push(rule);
  }

  removeRule(id: string): void {
    this.rules = this.rules.filter((r) => r.id !== id);
  }

  toggleRule(id: string, enabled: boolean): void {
    const rule = this.rules.find((r) => r.id === id);
    if (rule) rule.enabled = enabled;
  }

  toJSON(): string {
    return JSON.stringify(this.rules, null, 2);
  }

  static fromJSON(json: string): RulesEngine {
    const rules: Rule[] = JSON.parse(json);
    return new RulesEngine(rules);
  }

  private contextToRecord(ctx: RuleContext): Record<string, string | number | boolean> {
    return {
      currentFreq: ctx.currentFreq,
      currentAmp: ctx.currentAmp,
      dcVoltage: ctx.dcVoltage,
      heatsinkTemp: ctx.heatsinkTemp,
      load: ctx.load,
      imbalance: ctx.imbalance,
      fanMainHours: ctx.fanMainHours,
      fanControlHours: ctx.fanControlHours,
      daysOffline: ctx.daysOffline,
      voltageDrop: ctx.voltageDrop,
      looseTerminal: ctx.looseTerminal,
      cloggedHeatsink: ctx.cloggedHeatsink,
      nominalCurrent: ctx.nominalCurrent,
      nominalDC: ctx.nominalDC,
      maxTemp: ctx.maxTemp,
    };
  }
}
